import { useCallback, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import SOWForm from '../SOWForm';
import BudgetForm from '../BudgetForm';
import { FormFieldsData, useFormContext } from 'framework/form';
import ProjectConfigurationForm from '../ProjectConfigurationForm';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import TeamInputList from '../../сomponents/TeamInputList';
import OpportunitySelector from '../../сomponents/OpportunitySelector';
import CustomerReportForm from '../CustomerReportForm';
import {
  BudgetsType,
  isDocusignLinkValid,
  PROJECT_SCENARIO,
  ProjectSearchDto,
  useProjectFinanceService,
} from 'domain/shared/newProject';
import { Loading } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import FormElement from '../common/FormElement';
import { merge } from 'lodash';
import { getDefaultScenario, getProjectScenarioConfiguration } from './utils/projectScenario';
import { useProjectInitiationInputData } from '../../hooks/useProjectInitiationInputData';
import useProjectTeamService from 'domain/shared/newProject/team/useProjectTeamService';

const ProjectInitiationFormContent = () => {
  const { data, onChangeCallback, isSubmitEnabled } = useFormContext();
  const { addMessage, cleanMessage } = useMessageService();
  const { getProjectInitiationInputData } = useProjectInitiationInputData();
  const [isLoading, setIsLoading] = useState(false);
  const { getProjectTeamByProjectId } = useProjectTeamService();
  const { getBudget } = useProjectFinanceService();

  const onInputDataChange = (accountId?: string, opportunityId?: string, rawProjectId?: string) => {
    setIsLoading(true);
    onChangeCallback?.({ isProjectAvailable: false });
    getProjectInitiationInputData({ accountId, opportunityId, rawProjectId })
      .then(({ account, presaleOutput, opportunity, relatedProject, inputData }) => {
        const defaultScenario = getDefaultScenario(opportunity.isContractRenewal, relatedProject?.engagementType);

        const initTeamExpectedStructure = (presaleOutput?.requiredResources || []).map((element: any) => ({
          ...element,
          requiredRole: element.requiredRole || element.teamRole,
          competencyLevel: element.level || element.competencyLevel || element.maxLevel,
          requiredFte: element.requiredFte
            ? rawProjectId
              ? element.requiredFte
              : Number(element.requiredFte / 100).toFixed(2)
            : 0,
          _isExistingSOWReplacementPosition: false,
        }));

        const calculatedFormData = {
          project: {
            scenario: null,
            existingProject: relatedProject
              ? {
                  id: relatedProject.id,
                  name: relatedProject.name,
                  sowId: relatedProject.sowId,
                  defaultBudgetId: relatedProject.defaultBudgetId,
                }
              : null,
            projectCodeName:
              opportunity.hasAccountCodeName && !opportunity.accountHasProjects ? opportunity.accountName : '',
            originalName: account.originalName,
          },
          sow: {
            sowId: data?.urlParams?.docusignLink ? Number(account?.lastUploadedSowNumber || 0) + 1 : null,
            isDocusignLink: isDocusignLinkValid(data?.urlParams?.docusignLink),
            docusignLink: data?.urlParams?.docusignLink,
          },
          team: {
            expectedStructure: initTeamExpectedStructure.length ? initTeamExpectedStructure : [{}],
          },
          budget: {
            expectedSowSigningDate: data?.urlParams?.plannedSowSigningDate || null,
          },
          init: {
            expectedStructure: initTeamExpectedStructure.length ? initTeamExpectedStructure : [{}],
          },
          inputData,
          opportunity: opportunity || {},
        };

        onChangeCallback?.(merge({}, calculatedFormData, getProjectScenarioConfiguration(defaultScenario)));
        cleanMessage();
      })
      .catch(() => {
        addMessage(new ErrorMessage(`Error: loading team structure or account`));
      })
      .finally(() => {
        setIsLoading(false);
        onChangeCallback?.({ isProjectAvailable: true });
      });
  };

  const onRelatedProjectChange = useCallback(
    async (project: ProjectSearchDto, defaultBudgetId?: Nullable<StringOrNumber>) => {
      if (data?.project?.scenario === PROJECT_SCENARIO.REPLACEMENT) {
        const [team, budgetFormData] = await Promise.all([
          getProjectTeamByProjectId(project.id),
          defaultBudgetId ? getBudget(defaultBudgetId) : Promise.resolve({} as BudgetsType),
        ]);

        const budget = {
          'budget.billingType': budgetFormData?.billingType || null,
        };

        if ('isNeedCreateTeam' in team) {
          return { team: { expectedStructure: [{}] }, ...budget };
        }

        return {
          ...budget,
          team: {
            ...team,
            expectedStructure: (team.expectedStructure || []).map((position: FormFieldsData) => ({
              ...position,
              _isExistingSOWReplacementPosition: true,
            })),
          },
        };
      }

      return {};
    },
    [data?.project?.scenario, getBudget, getProjectTeamByProjectId],
  );

  return (
    <>
      <Modal isOpen={!isSubmitEnabled && data?.project?.createDaVinciRedmine === true} onToggle={() => {}}>
        <Stack alignItems={'center'} gap={2} py={2}>
          <Loading isInitialPosition />
          <Typography>Project setup in DaVinci has started. This may take a few minutes.</Typography>
          <Typography>Once it’s done, you can move on to the next step.</Typography>
        </Stack>
      </Modal>
      <Stack spacing={2}>
        <OpportunitySelector source={'inputData'} onChange={onInputDataChange} disabled={isLoading} />

        {isLoading && <Loading />}
        {!isLoading && (
          <>
            <FormElement title={'Project configuration'}>
              <ProjectConfigurationForm
                source={'project'}
                accountIdSource={'inputData.accountId'}
                onScenarioChange={scenario => {
                  let revertedStructure = {};
                  if (scenario !== PROJECT_SCENARIO.REPLACEMENT) {
                    revertedStructure = {
                      team: {
                        expectedStructure: data?.init?.expectedStructure || [{}],
                      },
                    };
                  }

                  const scenarioConfiguration = getProjectScenarioConfiguration(scenario);
                  onChangeCallback?.(merge({}, data, revertedStructure, scenarioConfiguration));
                }}
                onRelatedProjectChange={onRelatedProjectChange}
              />
            </FormElement>

            <FormElement
              title={'Customer Report'}
              isVisible={(formData: any) => formData?.project?.createDaVinciRedmine === true}
            >
              <CustomerReportForm source={'customerReport'} />
            </FormElement>

            <FormElement title="SOW information" isVisible={(formData: any) => formData?.urlParams?.docusignLink}>
              <SOWForm source="sow" />
            </FormElement>

            <FormElement title="Budget information">
              <BudgetForm source="budget" expectedStructureSource="team.expectedStructure" />
            </FormElement>
            <FormElement isTeam title="Team structure">
              <TeamInputList source="team.expectedStructure" />
            </FormElement>
          </>
        )}
      </Stack>
    </>
  );
};

export default ProjectInitiationFormContent;
